import React, { useContext, useState, useMemo, useEffect } from 'react'
import { observer } from 'mobx-react-lite'
import { DraftContext } from '../../../../../contexts/DraftContext'
import { useSidebarCollapse } from '../../../../../contexts/SidebarCollapseContext'
import { Check2, ChatLeftDots, FileEarmarkText, FileBarGraph } from 'react-bootstrap-icons'
import axiosService from '../../../../../utils/AxiosService'
import { useParams } from 'react-router-dom'
import { toastSuccess, toastError } from '../../../../FormComponents/Toasts'
import UploadSelectPanel from './ReviewWindowParts/UploadSelectPanel'
import Action from '../../../../../models/Action'
import DraftUpload from '../../../../../models/DraftUpload'
import ImageWindow from './ReviewWindowParts/ImageWindow'
import VideoWindow from './ReviewWindowParts/VideoWindow'
import NoUploadsImageWindow from './NoUploadsImageWindow'
import CaptionUpdateForm from './ReviewWindowParts/CaptionUpdateForm'
import CommentsSidebar from './ReviewWindowParts/CommentsSidebar'
import InsightsSidebar from './ReviewWindowParts/InsightsSidebar'
import ActionInsight from '../../../../../models/ActionInsight'
import OutsideClickClose from '../../../../OutsideClickClose'
import { useReadOnly } from '../../../../../contexts/ReadOnlyContext'

export default observer(function DraftReviewWindow() {
  const readOnly = useReadOnly()
  const { id, action_id } = useParams()
  const { draftState, actions, selectedUpload } = useContext(DraftContext)
  const { isCollapsed } = useSidebarCollapse()
  const currentAction = useMemo(() => actions.find(action => +action.id === +action_id) || new Action(), [action_id, actions, actions.length])
  const [showCommentsSidebar, setShowCommentsSidebar] = useState(false)
  const [showInsightsSidebar, setShowInsightsSidebar] = useState(false)

  const updateToAcceptAction = () => {
    axiosService.patch(
      `/api/project_manager/campaigns/${id}/actions/${action_id}`,
      {action_data: { draft_accepted: !currentAction.draftAccepted } }
    ).then((updatedAction) => {
      currentAction.draftAccepted = updatedAction.draft_accepted
      currentAction.action_insight = new ActionInsight(updatedAction.action_insight)
      updatedAction.draft_accepted ? toastSuccess('This action is accpeted!') : toastSuccess('This action is no longer accepted!')
    }).catch(() => toastError('Unable to accept action at this time.'))
  }

  // Refetch draft_uploads when opening an action so images get fresh URLs (fixes first-load not showing)
  useEffect(() => {
    if (!currentAction.id || !id || !action_id) return
    const hasUploadsWithUrl = currentAction.draft_uploads.some(u => u.url)
    if (currentAction.draft_uploads.length > 0 && hasUploadsWithUrl) return

    axiosService.get(`/api/project_manager/campaigns/${id}/actions/${action_id}/draft_uploads`)
      .then((uploads) => {
        const newUploads = uploads.map(upload => new DraftUpload(upload))
        currentAction.draft_uploads = newUploads
        if (newUploads.length > 0) {
          draftState.setSelectedUpload(newUploads[0])
        } else {
          draftState.setSelectedUpload(new DraftUpload())
        }
      })
      .catch(() => {})
  }, [currentAction.id, id, action_id])

  useEffect(() => {
    if (currentAction.id) {
      if (currentAction.draft_uploads.length > 0) {
        draftState.setSelectedUpload(currentAction.draft_uploads[0])
      } else {
        draftState.setSelectedUpload(new DraftUpload())
      }
    }
  }, [currentAction.id, currentAction.draft_uploads.length])

  const leftPosition = isCollapsed ? 479 : 575
  const width = isCollapsed ? 'calc(100vw - 479px)' : 'calc(100vw - 575px)'

  return (
    <div
      className='fixed top-[52px] flex flex-col justify-center items-center overflow-y-auto transition-all duration-300'
      style={{
        left: `${leftPosition}px`,
        height: 'calc(100vh - 52px)',
        width: width
      }}
    >
      <div className="h-full">
        <div className='my-5 p-3 rounded-lg bg-white dark:bg-rb-dark-mode flex justify-between items-center w-[650px] border-1 border-primary dark:border-rb-dark-mode-lighter'>
          <h2 className='font-bold text-xl dark:text-white'>{currentAction.name}</h2>
          <div className='flex gap-2 items-center'>
            {currentAction.draft_is_visible && currentAction.advertiser_approved && <p className='text-sm font-semibold text-rb-green dark:text-rb-green'>Advertiser Approved</p>}
            <button
              className={`flex items-center ${currentAction.draftAccepted ? 'bg-rb-green text-white dark:bg-rb-green dark:text-white' : 'hover:bg-gray-100 dark:hover:bg-rb-dark-mode-lighter dark:text-white'} ${readOnly ? 'cursor-default' : ''} rounded-lg p-2`}
              onClick={updateToAcceptAction}
              disabled={readOnly}
          >
            <Check2 />
              <span className={`ml-1 text-sm font-bold ${currentAction.draftAccepted ? 'text-white' : ''}`}>{currentAction.draftAccepted ? 'Accepted' : 'Accept'}</span>
            </button>
          </div>
        </div>

        <UploadSelectPanel action={currentAction} />

        { !selectedUpload.id &&  <NoUploadsImageWindow /> }
        { selectedUpload.id && ['mov', 'mp4'].includes(selectedUpload.fileType) ? <VideoWindow /> : selectedUpload.id ? <ImageWindow /> : null}

        <div className='my-5 p-3 rounded-lg bg-white dark:bg-rb-dark-mode flex justify-between items-center w-[650px] border-1 border-primary dark:border-rb-dark-mode-lighter'>
          <div className='flex gap-2 items-center'>
            <button
              onClick={() => setShowCommentsSidebar(!showCommentsSidebar)}
              className='p-2 flex gap-2 items-center rounded-md hover:bg-gray-200 dark:hover:bg-rb-dark-mode-lighter dark:text-white'
            >
              <ChatLeftDots />
              <span>Comments</span>
            </button>
            {
              currentAction.action_insight && currentAction.draftAccepted && (
                <button 
                  onClick={() => setShowInsightsSidebar(!showInsightsSidebar)}
                  className='p-2 flex gap-2 items-center rounded-md hover:bg-gray-200 dark:hover:bg-rb-dark-mode-lighter dark:text-white'
                >
                  <FileBarGraph />
                  <span>Insights</span>
                </button>
              )
            }
          </div>
          <button className='flex gap-2 items-center px-2 py-1 rounded-md hover:bg-gray-200 dark:hover:bg-rb-dark-mode-lighter dark:text-white opacity-30' disabled={true}>
            <FileEarmarkText />
            <span>Export</span>
          </button>
        </div>

        <div className="pb-2">
          <CaptionUpdateForm action={currentAction} />
        </div>
        {showCommentsSidebar && (
          <OutsideClickClose isOpen={showCommentsSidebar} onClose={() => setShowCommentsSidebar(false)}>
            <CommentsSidebar onClose={() => setShowCommentsSidebar(false)} /> 
          </OutsideClickClose>
        )}
        {showInsightsSidebar && (
          <OutsideClickClose isOpen={showInsightsSidebar} onClose={() => setShowInsightsSidebar(false)}>
            <InsightsSidebar onClose={() => setShowInsightsSidebar(false)} insight={currentAction.action_insight} />
          </OutsideClickClose>
        )}
      </div>
    </div>
  )
})
